import { useMemo } from 'react';
import { aggregateLabTests, normalizeAggregatedResults } from '../util/GenericUtil';
import bloodGroupings from '../../lambda/health-recommendations/blood_groupings.json';

export function useFilteredTests({ bloodTestFiles, effectiveSelectedFile, selectedSystem, normalizeTestName, searchQuery = '' }) {
    // Aggregate all tests for system scoring (unfiltered)
    const allTests = useMemo(() => {
        if (!bloodTestFiles) return [];
        let tests = [];
        bloodTestFiles.forEach(file => {
            const testsWithDate = file.data.body.map(test => ({
                ...test,
                testdate: file.data.header.testdate
            }));
            tests.push(...testsWithDate);
        });
        return aggregateLabTests(tests, normalizeTestName).map(normalizeAggregatedResults);
    }, [bloodTestFiles, normalizeTestName]);

    const dedupeTests = (tests) => {
        const seen = {};
        const result = [];
        for (const test of tests) {
            const alias = normalizeTestName(test.alias || test.test_name);
            const key = alias.toLowerCase();
            if (seen[key]) continue;
            seen[key] = true;
            result.push({ ...test, alias });
        }
        return result;
    };

    // Filtered tests based on effective selection + system + search filters
    const filteredTests = useMemo(() => {
        if (!bloodTestFiles) return [];

        let filtered = [];
        if (effectiveSelectedFile === "all") {
            filtered = allTests;
        } else if (typeof effectiveSelectedFile === 'number' && bloodTestFiles[effectiveSelectedFile]) {
            // Stamp the file's testdate onto each row. Aggregated tests ("all")
            // already carry one per marker; doing the same here means every
            // consumer of filteredTests can rely on `testdate` being present —
            // notably the recommendations payload, which must send the real
            // test date rather than today's.
            const file = bloodTestFiles[effectiveSelectedFile];
            const fileTestdate = file?.data?.header?.testdate;
            filtered = dedupeTests(file?.data?.body || [])
                .map(test => ({ ...test, testdate: test.testdate ?? fileTestdate }));
        }

        // Apply system filter if one is selected
        if (selectedSystem && bloodGroupings[selectedSystem]) {
            const systemMarkers = Object.keys(bloodGroupings[selectedSystem].markers);
            filtered = filtered.filter(test => {
                const testName = test.alias || test.test_name;
                return systemMarkers.includes(testName);
            });
        }

        // Apply search filter (used by TestDetails)
        if (searchQuery.trim()) {
            const q = searchQuery.trim().toLowerCase();
            filtered = filtered.filter(test => {
                const name = (test.alias || test.test_name || '').toLowerCase();
                return name.includes(q);
            });
        }

        return filtered;
    }, [bloodTestFiles, effectiveSelectedFile, allTests, selectedSystem, searchQuery, normalizeTestName]);

    // Unfiltered tests for export (without category/tag/search filters)
    const testsForExport = useMemo(() => {
        if (!bloodTestFiles) return [];

        if (effectiveSelectedFile === "all") {
            return allTests;
        } else if (typeof effectiveSelectedFile === 'number' && bloodTestFiles[effectiveSelectedFile]) {
            return bloodTestFiles[effectiveSelectedFile]?.data?.body || [];
        }

        return [];
    }, [bloodTestFiles, effectiveSelectedFile, allTests]);

    return { filteredTests, allTests, testsForExport };
}
